/**
 * MaoAvatar - Visual identity for the AI assistant
 *
 * A small avatar/icon that represents Mao throughout the app.
 * Uses the Sparkles icon with optional pulse animation.
 */

import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MaoSize } from '@/lib/mao';

interface MaoAvatarProps {
  /** Size variant */
  size?: MaoSize;
  /** Whether to show the pulse animation */
  animated?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CLASSES: Record<MaoSize, { container: string; icon: string }> = {
  sm: { container: 'w-6 h-6', icon: 'w-3 h-3' },
  default: { container: 'w-8 h-8', icon: 'w-4 h-4' },
  lg: { container: 'w-10 h-10', icon: 'w-5 h-5' },
};

export function MaoAvatar({
  size = 'default',
  animated = false,
  className,
}: MaoAvatarProps) {
  const sizes = SIZE_CLASSES[size];

  return (
    <div
      className={cn(
        'rounded-full bg-gradient-to-br from-lavender-400 to-lavender-600 flex items-center justify-center shadow-sm',
        sizes.container,
        animated && 'mao-pulse',
        className,
      )}
      aria-hidden="true"
    >
      <Sparkles className={cn('text-white', sizes.icon)} />
    </div>
  );
}
